import { isEqual, noop } from "lodash-es";
import type { ComputedRef, Ref } from "vue";
import { computed, ref, watch } from "vue";

import { sendStickyNoteAction } from "@/composables/useEventBus";
import { useActivityStore } from "@/store/activity";
import { useBoardStore } from "@/store/board";

const expandedItemId = ref<string | null>(null);
const enlargedStickyId = ref<string | null>(null);

export function useStickyChangeEnlarge(disabled = false): {
  expandedItemId: Ref<string | null> | ComputedRef<null>;
  handleExpanded: (opened: boolean, stickyId: string, itemId?: string) => void;
} {
  if (disabled) {
    return {
      expandedItemId: computed(() => null),
      handleExpanded: () => noop,
    };
  }

  watch(
    () => ({ ...useActivityStore().allFilters, tab: useActivityStore().tab }),
    (newState, prevState) => {
      const board = useBoardStore().board;
      const somethingChanged = !isEqual(newState, prevState);

      if (board && somethingChanged) {
        expandedItemId.value = null;
        if (enlargedStickyId.value) {
          sendStickyNoteAction(enlargedStickyId.value, { action: "shrink" });
          enlargedStickyId.value = null;
        }
      }
    },
    { immediate: true },
  );

  watch(
    () => useBoardStore().enlargedStickyNoteId,
    (newState) => {
      if (newState === null) {
        expandedItemId.value = null;
        enlargedStickyId.value = null;
      }
    },
  );

  function handleExpanded(opened: boolean, stickyId: string, itemId?: string) {
    const isDifferentItem = expandedItemId.value !== itemId;
    const isDifferentSticky = enlargedStickyId.value !== stickyId;

    if (!itemId) itemId = stickyId;

    if (opened) {
      if (
        expandedItemId.value !== null &&
        isDifferentItem &&
        enlargedStickyId.value &&
        isDifferentSticky
      ) {
        sendStickyNoteAction(enlargedStickyId.value, { action: "shrink" });
      }

      expandedItemId.value = itemId;
      enlargedStickyId.value = stickyId;

      sendStickyNoteAction(stickyId, {
        action: "enlarge",
        focus: false,
        trigger: "sticky-changes-item-selected",
      });
      return;
    }

    if (expandedItemId.value === itemId) {
      expandedItemId.value = null;
      enlargedStickyId.value = null;
      sendStickyNoteAction(stickyId, { action: "shrink" });
    }
  }

  return { expandedItemId, handleExpanded };
}
